"use client";

import { useEffect } from "react";
import { usePathname } from "next/navigation";
import { DEFAULT_LANG, GOOGLE_TRANSLATE_LANGS, readGoogTrans, retranslate } from "@/lib/googleTranslate";

declare global {
  interface Window {
    googleTranslateElementInit?: () => void;
    google?: {
      translate?: {
        TranslateElement?: {
          new (opts: Record<string, unknown>, id: string): unknown;
          InlineLayout?: { SIMPLE: unknown };
        };
      };
    };
  }
}

const SCRIPT_ID = "google-translate-script";
const WIDGET_ID = "google_translate_element";

/**
 * Guards React against Google Translate's DOM rewriting.
 *
 * Google swaps the text nodes React rendered for its own translated ones.
 * React still holds references to the originals, so the next time it tries to
 * update or unmount that subtree it calls `removeChild` with a node whose
 * parent has changed underneath it and throws
 * `NotFoundError: Failed to execute 'removeChild' on 'Node'`, which unmounts
 * the whole tree — a white screen.
 *
 * This is the long-standing community workaround (React issue #11538): make
 * the two mutating methods no-op instead of throwing when the parent doesn't
 * match. Be honest about the cost — it is monkey-patching a DOM built-in, and
 * it will also swallow the same error when it comes from a genuine React bug
 * rather than from Google. The warning is left in so those don't disappear
 * silently. It is installed once, on the client, and only on the marketing
 * pages that mount the widget.
 */
function installTranslateDomGuard() {
  const w = window as unknown as { __kaympusTranslateGuard?: boolean };
  if (w.__kaympusTranslateGuard) return;
  w.__kaympusTranslateGuard = true;

  const originalRemoveChild = Node.prototype.removeChild;
  Node.prototype.removeChild = function removeChild<T extends Node>(this: Node, child: T): T {
    if (child.parentNode !== this) {
      console.warn("[translate] skipped removeChild on a re-parented node", child);
      return child;
    }
    return originalRemoveChild.call(this, child) as T;
  };

  const originalInsertBefore = Node.prototype.insertBefore;
  Node.prototype.insertBefore = function insertBefore<T extends Node>(
    this: Node,
    newNode: T,
    referenceNode: Node | null
  ): T {
    if (referenceNode && referenceNode.parentNode !== this) {
      console.warn("[translate] skipped insertBefore on a re-parented reference", referenceNode);
      return newNode;
    }
    return originalInsertBefore.call(this, newNode, referenceNode) as T;
  };
}

/**
 * Mounts the Google Website Translator once per session.
 *
 * The widget itself is rendered off-screen rather than hidden with
 * `display: none` — Google's script measures its container and refuses to
 * initialise the `<select>` in a box with no layout. `LanguageToggle` then
 * drives that hidden `<select>`, so visitors see the site's own EN/हिं pill
 * instead of Google's dropdown and banner.
 *
 * `autoDisplay: false` stops it translating on load based on browser locale;
 * the only thing that changes the language is the `googtrans` cookie, which
 * the toggle owns.
 *
 * NOTHING HERE RUNS FOR AN ENGLISH VISITOR.
 *
 * The widget used to be loaded on every marketing page for everyone, and it
 * is not cheap: ~100KB of third-party JS, a ~140ms long task, a third-party
 * cookie, and — the expensive part — `installTranslateDomGuard`, which
 * replaces `Node.prototype.removeChild` and `insertBefore` with wrappers that
 * run an extra parent check. Those two are on the hot path of every DOM
 * mutation React makes, for the whole life of the page, on the whole site.
 *
 * It is all dead weight unless the page is actually being translated, and the
 * gate is exact rather than a guess: `setGoogleTranslateLanguage` writes the
 * `googtrans` cookie and then reloads, always, in both directions (see the
 * note on it). So by the time a Hindi page renders, the cookie is already set
 * and this mounts the widget on that load. There is no path where a visitor
 * needs the widget and the cookie is not there to say so.
 */
export default function GoogleTranslate() {
  const pathname = usePathname();

  useEffect(() => {
    // Keep <html lang> honest for screen readers, which pick pronunciation
    // from it — reading Devanagari with an English voice is unintelligible.
    const active = readGoogTrans();
    if (active) document.documentElement.lang = active;

    // English — the widget has nothing to do, so it is never loaded and the
    // DOM guard is never installed.
    if (!active || active === DEFAULT_LANG) return;

    installTranslateDomGuard();

    if (document.getElementById(SCRIPT_ID)) return;

    window.googleTranslateElementInit = () => {
      const TranslateElement = window.google?.translate?.TranslateElement;
      if (!TranslateElement) return;
      new TranslateElement(
        {
          pageLanguage: "en",
          includedLanguages: GOOGLE_TRANSLATE_LANGS.join(","),
          autoDisplay: false,
        },
        WIDGET_ID
      );
    };

    const script = document.createElement("script");
    script.id = SCRIPT_ID;
    script.src = "https://translate.google.com/translate_a/element.js?cb=googleTranslateElementInit";
    script.async = true;
    document.body.appendChild(script);
  }, []);

  /* A client-side navigation swaps the page body without a document load, so
     the widget never sees the new content and the destination renders in
     English. Nudge it once the new route has painted. The delay is for the
     App Router's own commit — re-scanning before React has finished gives the
     widget the outgoing page. */
  useEffect(() => {
    const active = readGoogTrans();
    if (!active || active === DEFAULT_LANG) return;

    const id = setTimeout(() => retranslate(active), 400);
    return () => clearTimeout(id);
  }, [pathname]);

  return <div id={WIDGET_ID} aria-hidden className="gt-widget-host" />;
}
